import { Box, Skeleton, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import colors from "../../../config/colors";
import { EnabledFeatures, TeamSnapshot } from "../store/supervisor-home-types";
import RailCard from "./rail-card";

interface TeamTodayCardProps {
	team?: TeamSnapshot;
	enabledFeatures?: EnabledFeatures;
	isLoading: boolean;
}

function TeamTodayCard({
	team,
	enabledFeatures,
	isLoading,
}: TeamTodayCardProps) {
	const { t } = useTranslation();

	const rows: { key: string; label: string; value: number }[] = team
		? [
			{
				key: "onShift",
				label: t("supervisor-home_team-on-shift"),
				value: team.onShiftToday,
			},
			{
				key: "off",
				label: t("supervisor-home_team-off-today"),
				value: team.offToday,
			},
			{
				key: "noJob",
				label: t("supervisor-home_team-no-job"),
				value: team.noJobAssigned,
			},
			// expiredJob is only meaningful when job expiry is on; otherwise the
			// endpoint returns 0 to mean "not applicable".
			...(enabledFeatures?.jobExpiry
				? [
					{
						key: "expiredJob",
						label: t("supervisor-home_team-expired-job"),
						value: team.expiredJob,
					},
				]
				: []),
			{
				key: "total",
				label: t("supervisor-home_team-total"),
				value: team.total,
			},
		]
		: [];

	return (
		<RailCard title={t("supervisor-home_team-today")}>
			{isLoading || !team ? (
				<Skeleton animation='wave' height={140} />
			) : (
				<>
					{rows.map((row, index) => (
						<Box
							key={row.key}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "8px 0",
								borderBottom:
									index === rows.length - 1
										? "none"
										: "1px solid rgba(0, 0, 0, 0.08)",
							}}
						>
							<Typography sx={{ fontSize: "14px", color: colors.title }}>
								{row.label}
							</Typography>
							<Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
								{row.value}
							</Typography>
						</Box>
					))}
					<Typography
						sx={{
							mt: 1.5,
							fontSize: "12px",
							color: colors.gray,
							fontStyle: "italic",
						}}
					>
						{t("supervisor-home_team-scope-note")}
					</Typography>
				</>
			)}
		</RailCard>
	);
}

export default TeamTodayCard;
